import type React from "react";
import { createContext, useContext } from "react";

export type DerivedTableDataSourceState =
  | { status: "no-options" }
  | { status: "pending" }
  | { status: "error" }
  | { status: "ready"; rows: Record<string, unknown>[] };

export interface DerivedTableDataSourceMessages {
  /** Shown when the currently selected table has no instance to show data for (or no data was
   * ever produced for it) - what this means concretely differs by data source. */
  noOptionsTitleKey: string;
  noOptionsDescriptionKey: string;
  pendingTitleKey: string;
  pendingDescriptionKey: string;
  errorTitleKey: string;
  errorDescriptionKey: string;
}

export interface DerivedTableDataSourceValue {
  /** Instance picker options for the currently selected table - empty when there's nothing to pick from yet. */
  instanceOptions: { value: string; label: string }[];
  state: DerivedTableDataSourceState;
  messages: DerivedTableDataSourceMessages;
}

const Ctx = createContext<DerivedTableDataSourceValue | null>(null);

/**
 * How DerivedTableToolbarControls/DerivedTableResultGrid get their data - deliberately abstracted
 * out so the exact same presentation components work whether the data comes from a live solve
 * (the debugger, via LiveDerivedTableDataSourceProvider) or a stored, already-computed artifact
 * (the Session Report's Visualisations tab, via StoredDerivedTableDataSourceProvider). Each
 * provider owns its own fetching strategy and decides what each DerivedTableDataSourceState means
 * in its context (eg. "no-options" is "no instances of this entity exist yet" for the live source,
 * but "nothing was ever persisted for this table" for the stored source).
 */
export const DerivedTableDataSourceProvider = ({
  value,
  children,
}: React.PropsWithChildren<{ value: DerivedTableDataSourceValue }>) => <Ctx.Provider value={value}>{children}</Ctx.Provider>;

export const useDerivedTableDataSource = (): DerivedTableDataSourceValue => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDerivedTableDataSource must be used within a DerivedTableDataSourceProvider");
  return ctx;
};
